/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Circle, Maximize2, Mic, MicOff, Minimize2, PhoneOff, Square, Video, VideoOff } from 'lucide-react';
import Modal from './Modal';

const LiveSessionCallModal = ({
  room = null,
  connectionStatus = 'idle',
  micEnabled = false,
  camEnabled = false,
  recordingStatus = 'idle',
  recordingElapsedMs = 0,
  recordingSupported = false,
  onToggleMic,
  onToggleCam,
  onStartRecording,
  onStopRecording,
  onLeave,
  attachLocalVideo,
  attachRemoteVideo,
  localName = null,
  remoteName = null,
}) => {
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Track the local camera's real pixel ratio so the PIP box is never forced into 16:9.
  const [localAspectRatio, setLocalAspectRatio] = useState(16 / 9);

  // Reset the fullscreen preference whenever a call ends, so the next call always starts sized normally.
  useEffect(() => {
    if (!room) { setIsFullscreen(false); }
  }, [room]);

  // Bind once the modal (and its <video> elements) mount, and unbind only on unmount —
  // detaching on every camEnabled/connectionStatus flip (e.g. toggling the camera off)
  // tells the SDK to release the binding entirely, so re-enabling later never re-attaches.
  useEffect(() => {
    if (!room) { return undefined; }
    attachRemoteVideo(remoteVideoRef.current);
    return () => attachRemoteVideo(null);
  }, [attachRemoteVideo, room]);

  useEffect(() => {
    if (!room) { return undefined; }
    attachLocalVideo(localVideoRef.current);
    return () => attachLocalVideo(null);
  }, [attachLocalVideo, room]);

  // Refresh-only: re-poke the binding once a track actually becomes ready, without
  // ever detaching — covers the case where attach ran before the SDK had a stream yet.
  useEffect(() => {
    if (room && connectionStatus === 'open') {
      attachRemoteVideo(remoteVideoRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionStatus]);

  // Re-enabling the camera adds a fresh video track to the *same* MediaStream without
  // removing the previous (now-ended) one, and the element keeps rendering that stale
  // first track. Poll briefly for the new live track and rebuild the stream around it.
  useEffect(() => {
    if (!(room && camEnabled)) { return undefined; }
    attachLocalVideo(localVideoRef.current);
    const el = localVideoRef.current;

    const dropStaleVideoTracks = () => {
      const stream = el?.srcObject;
      if (!(stream instanceof MediaStream)) { return false; }
      const videoTracks = stream.getVideoTracks();
      const liveVideoTracks = videoTracks.filter((t) => t.readyState === 'live');
      if (!liveVideoTracks.length || liveVideoTracks.length === videoTracks.length) { return false; }
      el.srcObject = new MediaStream([...liveVideoTracks, ...stream.getAudioTracks()]);
      return true;
    };

    if (dropStaleVideoTracks()) { return undefined; }
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (dropStaleVideoTracks() || attempts >= 6) {
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [attachLocalVideo, room, camEnabled]);

  const remoteConnected = connectionStatus === 'open';
  // While the remote side isn't connected yet, show the local preview full-size (if the
  // camera is on) instead of a PiP over an empty remote frame; once remote is connected
  // it always takes over the main view, with local (if on) shrinking to a PiP.
  const localIsMain = camEnabled && !remoteConnected;

  const isRecording = recordingStatus === 'recording';
  const isRecordingTransitional = recordingStatus === 'finalizing' || recordingStatus === 'uploading';
  const recordingDisabled = isRecordingTransitional; // don't disable during 'idle'/'recording'/'uploaded'/'failed'

  const formatElapsed = (ms) => {
    const totalSeconds = Math.floor((ms || 0) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <Modal
      isOpen={!!room}
      onClose={onLeave}
      title={`Live Session · ${room}`}
      className={isFullscreen
        // Fullscreen: fill the viewport, no scroll ever.
        ? 'w-screen h-screen max-w-none rounded-none overflow-hidden'
        // Normal: fixed height so overflow:hidden suppresses any scrollbar.
        : 'w-[75vw] max-w-[75vw] h-[75vh] overflow-hidden'}
    >
      {/*
       * flex-col + gap-4: header (from Modal) above; this div fills remaining height.
       * flex-1 + min-h-0: grow to fill the Modal's fixed height and allow shrinking.
       */}
      <div className="flex flex-col gap-4" style={{ height: isFullscreen ? 'calc(100vh - 180px)' : 'calc(75vh - 180px)' }}>

        {/*
         * Video pane: position:relative establishes the stacking context.
         * flex-1 + min-h-0 lets it consume all vertical space in the flex column.
         * overflow-hidden clips the PIP to the pane edges.
         * NO display:flex here — flex with alignItems:center would collapse height:100% children.
         */}
        <div
          className="relative w-full rounded-2xl overflow-hidden bg-base-300"
          style={{ flex: 1, minHeight: 0 }}
        >
          {/* ── Remote stream: absolutely fills the pane, ratio preserved by contain ── */}
          <div
            className={localIsMain ? 'hidden' : ''}
            style={{ position: 'absolute', inset: 0 }}
          >
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="bg-black"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            {remoteConnected && remoteName && (
              <div className="absolute bottom-2 left-2 z-10 max-w-[70%] truncate rounded bg-black/60 px-2 py-1 text-xs font-bold text-white">
                {remoteName}
              </div>
            )}
          </div>

          {/* ── Waiting / connecting overlay ── */}
          {!localIsMain && !remoteConnected && (
            <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-base-content/40 uppercase tracking-widest bg-base-300/80">
              {connectionStatus === 'connecting' || connectionStatus === 'reconnecting' ? 'Connecting…' : 'Waiting for stream…'}
            </div>
          )}

          {/*
           * Local video:
           *  - localIsMain: fills the entire pane (before remote joins)
           *  - PIP mode:    position:absolute, bottom-right corner, width % of pane.
           *                 aspectRatio is read from the real camera via onLoadedMetadata
           *                 so the box never squashes portrait or ultra-wide cameras.
           *                 object-fit:contain shows the full frame without cropping.
           */}
          <div
            style={localIsMain ? {
              position: 'relative',
              width: '100%',
              height: '100%',
            } : {
              display: camEnabled ? 'block' : 'none',
              position: 'absolute',
              bottom: 12,
              right: 12,
              zIndex: 10,
              width: '22%',
              maxWidth: 240,
              minWidth: 90,
              aspectRatio: String(localAspectRatio),
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.15)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.55)',
              background: '#000',
            }}
          >
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                background: '#000',
              }}
              onLoadedMetadata={(e) => {
                const vid = e.currentTarget;
                if (vid.videoWidth && vid.videoHeight) {
                  setLocalAspectRatio(vid.videoWidth / vid.videoHeight);
                }
              }}
            />
            {localName && (
              <div className="absolute bottom-1 left-1 z-10 max-w-[80%] truncate rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {localName}
              </div>
            )}
          </div>
        </div>

        {/* ── Controls bar ── */}
        <div className="flex items-center justify-center gap-3 flex-shrink-0">
          <button
            onClick={onToggleMic}
            className={`p-3 rounded-full transition-all ${micEnabled ? 'bg-base-200 text-base-content hover:bg-base-300' : 'bg-error text-error-content hover:bg-error/80'}`}
            title={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
          >
            {micEnabled ? <Mic size={18} /> : <MicOff size={18} />}
          </button>
          <button
            onClick={onToggleCam}
            className={`p-3 rounded-full transition-all ${camEnabled ? 'bg-base-200 text-base-content hover:bg-base-300' : 'bg-error text-error-content hover:bg-error/80'}`}
            title={camEnabled ? 'Turn camera off' : 'Turn camera on'}
          >
            {camEnabled ? <Video size={18} /> : <VideoOff size={18} />}
          </button>
          {recordingSupported && (
            <button
              onClick={isRecording ? onStopRecording : onStartRecording}
              disabled={recordingDisabled}
              className={`relative p-3 rounded-full transition-all flex items-center gap-2 ${
                isRecording
                  ? 'bg-error text-error-content hover:bg-error/80'
                  : 'bg-base-200 text-base-content hover:bg-base-300'
              } ${recordingDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={
                isRecordingTransitional
                  ? (recordingStatus === 'finalizing' ? 'Finalizing recording...' : 'Uploading recording...')
                  : (isRecording ? 'Stop recording' : 'Start recording')
              }
            >
              {isRecording ? (
                <>
                  <Square size={18} className="fill-current" />
                  <span className="text-xs font-bold tabular-nums pr-1">{formatElapsed(recordingElapsedMs)}</span>
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                </>
              ) : (
                <Circle size={18} className={recordingStatus === 'failed' ? 'text-error' : 'fill-current text-red-500'} />
              )}
            </button>
          )}
          <button
            onClick={() => setIsFullscreen((prev) => !prev)}
            className="p-3 rounded-full bg-base-200 text-base-content hover:bg-base-300 transition-all"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
          <button
            onClick={onLeave}
            className="p-3 rounded-full bg-error text-error-content hover:bg-error/80 transition-all"
            title="Leave session"
          >
            <PhoneOff size={18} />
          </button>
        </div>
      </div>
    </Modal>
  );
};

LiveSessionCallModal.propTypes = {
  room: PropTypes.string,
  connectionStatus: PropTypes.string,
  micEnabled: PropTypes.bool,
  camEnabled: PropTypes.bool,
  recordingStatus: PropTypes.string,
  recordingElapsedMs: PropTypes.number,
  recordingSupported: PropTypes.bool,
  onToggleMic: PropTypes.func.isRequired,
  onToggleCam: PropTypes.func.isRequired,
  onStartRecording: PropTypes.func.isRequired,
  onStopRecording: PropTypes.func.isRequired,
  onLeave: PropTypes.func.isRequired,
  attachLocalVideo: PropTypes.func.isRequired,
  attachRemoteVideo: PropTypes.func.isRequired,
  localName: PropTypes.string,
  remoteName: PropTypes.string,
};

export default LiveSessionCallModal;
